import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import type { SystemDetail } from '../api/types';
import { RolePill } from '../components/RolePill';
import { StatusPill } from '../components/StatusPill';
import { relativeTime, shortDate } from '../utils/format';

const KIND_LABELS: Record<string, string> = {
  KeycloakClient: 'Keycloak client roles',
  Outline: 'Outline API (email invite)',
  Gitea: 'Gitea organization team',
};

export function SystemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [system, setSystem] = useState<SystemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setSystem(await api.system(id));
      setError(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setError('System not found.');
      } else {
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="page">
        <Link className="back-link" to="/systems">
          ← All systems
        </Link>
        <div className="error-banner">{error}</div>
      </div>
    );
  }

  if (!system) {
    return (
      <div className="page">
        <div className="empty-state">Loading system…</div>
      </div>
    );
  }

  return (
    <div className="page page-narrow">
      <Link className="back-link" to="/systems">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3L5 8l5 5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        All systems
      </Link>

      <div className="detail-header">
        <div className="sys-icon sys-icon-xl" style={{ background: system.color }}>
          {system.abbr}
        </div>
        <div className="detail-header-meta">
          <div className="detail-title-row">
            <h1>{system.name}</h1>
            {system.syncError ? (
              <span className="sync-badge sync-error" title={system.syncError}>
                <span className="sync-dot" />
                Sync error
              </span>
            ) : (
              <span className="sync-badge sync-ok">
                <span className="sync-dot" />
                Synced
              </span>
            )}
          </div>
          <div className="detail-subtitle">
            {system.category} · {system.userCount} users · {system.adminCount} admins ·
            last sync <span className="mono">{relativeTime(system.lastSyncAt)}</span>
          </div>
        </div>
      </div>

      {system.syncError && <div className="error-banner">{system.syncError}</div>}

      <div className="system-detail-grid">
        <div className="card roles-card">
          <div className="access-head">
            <div className="panel-label">Settable roles</div>
            <div className="mono muted">Granted from the user detail page</div>
          </div>
          {system.roles.map((role) => (
            <div key={role.name} className="role-row">
              <div className="role-row-head">
                <RolePill role={role.name} />
                {role.isAdminRole && <span className="role-flag">counts as admin</span>}
                <span className="role-row-count mono muted">
                  {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
                </span>
              </div>
              <div className="role-row-desc">{role.description}</div>
            </div>
          ))}
          {system.roles.length === 0 && (
            <div className="empty-state">This system defines no grantable roles.</div>
          )}
        </div>

        <div className="detail-side">
          <div className="panel">
            <div className="panel-label">Integration</div>
            <div className="field-list">
              <div>
                <div className="field-label">Managed through</div>
                {KIND_LABELS[system.kind] ?? system.kind}
              </div>
              {system.clientIds.length > 0 && (
                <div>
                  <div className="field-label">
                    {system.clientIds.length === 1 ? 'Keycloak client' : 'Keycloak clients'}
                  </div>
                  {system.clientIds.map((clientId) => (
                    <div key={clientId} className="mono">
                      {clientId}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="field-label">Last sync</div>
                <span className="mono">{relativeTime(system.lastSyncAt)}</span>
              </div>
            </div>
          </div>
          {system.notes && (
            <div className="panel">
              <div className="panel-label">Good to know</div>
              <div className="muted-note">{system.notes}</div>
            </div>
          )}
        </div>
      </div>

      <div className="card table-card system-members-card">
        <div className="access-head">
          <div className="panel-label">Who has access</div>
          <div className="mono muted">
            {system.members.length} {system.members.length === 1 ? 'assignment' : 'assignments'}
          </div>
        </div>
        <div className="table-scroll">
          <div className="members-grid members-grid-head">
            <div>User</div>
            <div>Role</div>
            <div title="Keycloak account status">Keycloak</div>
            <div>Granted</div>
            <div>Expires</div>
          </div>
          {system.members.map((member) => (
            <Link
              key={`${member.userId}-${member.role}`}
              to={`/users/${member.userId}`}
              className="members-grid members-grid-row"
            >
              <div className="user-cell-meta">
                <div className="user-cell-name">{member.name}</div>
                <div className="user-cell-email">{member.email ?? member.username}</div>
              </div>
              <div>
                <RolePill role={member.role} />
              </div>
              <div>
                <StatusPill status={member.status} />
              </div>
              <div className="mono muted">{shortDate(member.since)}</div>
              <div className="mono muted">{shortDate(member.expiresAt)}</div>
            </Link>
          ))}
          {system.members.length === 0 && (
            <div className="empty-state">Nobody has access to this system yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
